import { useEffect, useRef } from 'react';

/** Something the quick-create menu asks a screen to open once it is showing. */
export type QuickIntent = 'task' | 'report' | 'meeting';

let pending: { intent: QuickIntent; text?: string } | null = null;
const listeners = new Set<(i: QuickIntent, text?: string) => void>();

/** `text` starts the new item off, e.g. "חממה 3" from the search palette. */
export function requestQuick(intent: QuickIntent, text?: string) {
  pending = { intent, text };
  listeners.forEach((l) => l(intent, text));
}

/**
 * Runs `onIntent` when the quick-create menu asks for one of `kinds`, whether the screen
 * was already open or is mounting because of that request. A screen that only routes the
 * request to a child (which opens the editor) passes `consume: false`.
 */
export function useQuickIntent(
  kinds: readonly QuickIntent[],
  onIntent: (i: QuickIntent, text?: string) => void,
  { consume = true } = {},
) {
  const cb = useRef(onIntent);
  cb.current = onIntent;
  const key = kinds.join();
  useEffect(() => {
    const wanted = key.split(',');
    const handle = (i: QuickIntent, text?: string) => {
      if (!wanted.includes(i)) return;
      if (consume) pending = null;
      cb.current(i, text);
    };
    if (pending) handle(pending.intent, pending.text);
    listeners.add(handle);
    return () => {
      listeners.delete(handle);
    };
  }, [key, consume]);
}
